import { Menu, MenuItem } from "@material-ui/core";
import moment from "moment";
import React, { useState } from "react";
import { useSnoozeAlert } from "../../../hooks/react-query/alerts/useSnoozeAlert";

interface Props {
  alertId: string;
  // AlertCard's trigger is an icon-only button needing its own class; DashboardAlertWidget's
  // relies on a plain `button` descendant selector in its own scss, so it passes none - both
  // already have their own scss conventions for action buttons, left to the caller rather than
  // this component picking one look for both.
  triggerClassName?: string;
  triggerContent: React.ReactNode;
}

const SNOOZE_PRESETS: { label: string; amount: number; unit: "day" | "week" | "month" }[] = [
  { label: "1 day", amount: 1, unit: "day" },
  { label: "1 week", amount: 1, unit: "week" },
  { label: "1 month", amount: 1, unit: "month" },
];

// Presets only (no custom date picker) - confirmed as the preferred UX, and this codebase has no
// existing date-picker component to reach for anyway. Reuses @material-ui/core's Menu/MenuItem,
// the one existing dropdown-menu precedent in this codebase (Annotations.tsx's download menu),
// rather than introducing a second bespoke popover pattern.
export const AlertSnoozeMenu = ({ alertId, triggerClassName, triggerContent }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { mutate: snooze } = useSnoozeAlert();

  const handleSelect = (amount: number, unit: "day" | "week" | "month") => {
    snooze({ alertId, snoozedUntil: moment().add(amount, unit).toISOString() });
    setAnchorEl(null);
  };

  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label="Snooze"
        data-test-id={`alert-snooze-${alertId}`}
      >
        {triggerContent}
      </button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {SNOOZE_PRESETS.map((preset) => (
          <MenuItem
            key={preset.unit}
            onClick={() => handleSelect(preset.amount, preset.unit)}
            data-test-id={`alert-snooze-option-${alertId}-${preset.unit}`}
          >
            Snooze {preset.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
